import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../LoadingSpinner';
import './AuthPages.css';

const ResetPasswordPage: React.FC = () => {
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [hashHandled, setHashHandled] = useState(false);

  useEffect(() => {
    const rawHash = window.location.hash;
    if (!rawHash) {
      setHashHandled(true);
      return;
    }

    const params = new URLSearchParams(rawHash.replace(/^#/, ''));
    const type = params.get('type');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (type !== 'recovery' || !accessToken || !refreshToken) {
      setHashHandled(true);
      return;
    }

    let cancelled = false;

    (async () => {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (cancelled) return;
      if (sessionError) {
        setLinkError(sessionError.message);
      }
      window.history.replaceState(null, '', window.location.pathname);
      setHashHandled(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message);
      setSubmitting(false);
      return;
    }

    await supabase.auth.signOut();
    navigate('/login', { replace: true, state: { passwordReset: true } });
  };

  if (authLoading || !hashHandled) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (linkError || !session) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src="/dark_favicon_logo.png" alt="Gallop AI" className="auth-logo" />
            <h1>Reset link invalid</h1>
            <p>This password reset link is invalid or has expired.</p>
          </div>
          {linkError && <div className="error-message">{linkError}</div>}
          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-link">
              Request a new reset link
            </Link>
            {' · '}
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/dark_favicon_logo.png" alt="Gallop AI" className="auth-logo" />
          <h1>Set a new password</h1>
          <p>Choose a new password for your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              type="password"
              id="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-new-password">Confirm new password</label>
            <input
              type="password"
              id="confirm-new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Confirm your new password"
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          <button type="submit" disabled={submitting} className="auth-button primary">
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/login" className="auth-link">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
